import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Award, Eye, EyeOff } from 'lucide-react';
import { Button } from '../ui/button';
import { useToast } from '../../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const TrustBadgesManagement = () => {
  const { toast } = useToast();
  const [badges, setBadges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingBadge, setEditingBadge] = useState(null);
  const [formData, setFormData] = useState({
    badgeType: 'certificate',
    title: '',
    description: '',
    imageUrl: '',
    issuedBy: '',
    issuedDate: '',
    isActive: true
  });

  useEffect(() => {
    fetchBadges();
  }, []);

  const fetchBadges = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/trust-badges`);
      if (response.ok) {
        const data = await response.json();
        setBadges(data);
      }
    } catch (error) {
      console.error('Error fetching badges:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const url = editingBadge
        ? `${BACKEND_URL}/api/admin/trust-badges/${editingBadge.badgeId}`
        : `${BACKEND_URL}/api/admin/trust-badges`;
      
      const response = await fetch(url, {
        method: editingBadge ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        toast({ title: `Badge ${editingBadge ? 'updated' : 'created'} successfully` });
        setShowModal(false);
        setEditingBadge(null);
        resetForm();
        fetchBadges();
      }
    } catch (error) {
      toast({ variant: "destructive", title: "Operation failed" });
    }
  };

  const handleDelete = async (badge) => {
    if (!window.confirm('Delete this badge?')) return;

    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/trust-badges/${badge.badgeId}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        toast({ title: "Badge deleted successfully" });
        fetchBadges();
      }
    } catch (error) {
      toast({ variant: "destructive", title: "Failed to delete" });
    }
  };

  const toggleActive = async (badge) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/trust-badges/${badge.badgeId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ isActive: !badge.isActive })
      });

      if (response.ok) {
        toast({ title: "Status updated" });
        fetchBadges();
      }
    } catch (error) {
      toast({ variant: "destructive", title: "Failed to update status" });
    }
  };

  const openEditModal = (badge) => {
    setEditingBadge(badge);
    setFormData({
      badgeType: badge.badgeType,
      title: badge.title,
      description: badge.description,
      imageUrl: badge.imageUrl,
      issuedBy: badge.issuedBy || '',
      issuedDate: badge.issuedDate ? new Date(badge.issuedDate).toISOString().split('T')[0] : '',
      isActive: badge.isActive
    });
    setShowModal(true);
  };

  const resetForm = () => {
    setFormData({
      badgeType: 'certificate',
      title: '',
      description: '',
      imageUrl: '',
      issuedBy: '',
      issuedDate: '',
      isActive: true
    });
  };

  if (loading) {
    return <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div></div>;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-slate-800">Trust Badges</h2>
          <p className="text-slate-600">{badges.length} total badges</p>
        </div>
        <Button onClick={() => { resetForm(); setShowModal(true); }} className="bg-blue-600 hover:bg-blue-700">
          <Plus className="w-4 h-4 mr-2" />
          Add Badge
        </Button>
      </div>

      {/* Badges Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {badges.map((badge) => (
          <div key={badge.badgeId} className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
            <div className="flex items-start justify-between mb-4">
              <div className="flex-1">
                <div className="w-16 h-16 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                  <Award className="w-8 h-8 text-blue-600" />
                </div>
                <h3 className="font-semibold text-slate-800 mb-1">{badge.title}</h3>
                <span className={`inline-block px-2 py-1 rounded text-xs font-medium mb-2 ${
                  badge.isActive ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-700'
                }`}>
                  {badge.isActive ? 'Active' : 'Inactive'}
                </span>
              </div>
            </div>

            <p className="text-sm text-slate-600 mb-3">{badge.description}</p>

            <div className="space-y-2 mb-4 text-sm text-slate-500">
              <p><strong>Type:</strong> {badge.badgeType}</p>
              {badge.issuedBy && <p><strong>Issued By:</strong> {badge.issuedBy}</p>}
              {badge.issuedDate && (
                <p><strong>Issued:</strong> {new Date(badge.issuedDate).toLocaleDateString()}</p>
              )}
            </div>

            {badge.imageUrl && (
              <div className="mb-4">
                <img src={badge.imageUrl} alt={badge.title} className="w-full h-32 object-contain bg-slate-50 rounded-lg" />
              </div>
            )}

            <div className="flex items-center space-x-2">
              <Button
                size="sm"
                variant="outline"
                onClick={() => toggleActive(badge)}
                className="flex-1"
              >
                {badge.isActive ? <Eye className="w-3 h-3 mr-1" /> : <EyeOff className="w-3 h-3 mr-1" />}
                {badge.isActive ? 'Hide' : 'Show'}
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => openEditModal(badge)}
              >
                <Edit className="w-3 h-3" />
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => handleDelete(badge)}
                className="text-red-600"
              >
                <Trash2 className="w-3 h-3" />
              </Button>
            </div>
          </div>
        ))}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-lg w-full p-6">
            <h3 className="text-xl font-bold mb-4">{editingBadge ? 'Edit' : 'Create'} Trust Badge</h3>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Badge Type</label>
                  <select
                    value={formData.badgeType}
                    onChange={(e) => setFormData({...formData, badgeType: e.target.value})}
                    className="w-full px-4 py-2 border rounded-lg"
                  >
                    <option value="certificate">Certificate</option>
                    <option value="award">Award</option>
                    <option value="verification">Verification</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Status</label>
                  <select
                    value={formData.isActive}
                    onChange={(e) => setFormData({...formData, isActive: e.target.value === 'true'})}
                    className="w-full px-4 py-2 border rounded-lg"
                  >
                    <option value="true">Active</option>
                    <option value="false">Inactive</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Title</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({...formData, title: e.target.value})}
                  className="w-full px-4 py-2 border rounded-lg"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  className="w-full px-4 py-2 border rounded-lg"
                  rows="3"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Image URL</label>
                <input
                  type="url"
                  value={formData.imageUrl}
                  onChange={(e) => setFormData({...formData, imageUrl: e.target.value})}
                  className="w-full px-4 py-2 border rounded-lg"
                  placeholder="https://..."
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Issued By</label>
                <input
                  type="text"
                  value={formData.issuedBy}
                  onChange={(e) => setFormData({...formData, issuedBy: e.target.value})}
                  className="w-full px-4 py-2 border rounded-lg"
                  placeholder="Certifying Authority"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Issued Date</label>
                <input
                  type="date"
                  value={formData.issuedDate}
                  onChange={(e) => setFormData({...formData, issuedDate: e.target.value})}
                  className="w-full px-4 py-2 border rounded-lg"
                />
              </div>
              <div className="flex justify-end space-x-3">
                <Button type="button" variant="outline" onClick={() => { setShowModal(false); setEditingBadge(null); }}>
                  Cancel
                </Button>
                <Button type="submit" className="bg-blue-600 hover:bg-blue-700">
                  {editingBadge ? 'Update' : 'Create'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default TrustBadgesManagement;
